/**
 * ============================================================================
 * The deck for "Attest - Live Demonstration for A-B Tech" (22 Sep 2026)
 * ============================================================================
 * Brian's ruling from the 05:50 rehearsal: a bare list of questions is not a
 * class. Each concept gets a slide that TEACHES the point first, the push
 * buttons sit at the foot of that slide, and the reveal happens on the slide
 * once the item is pushed. Order is his: opening, attestation, affiliation,
 * capture, the 10% date, where the record lives, what changes for you,
 * concerns, the final, the close.
 *
 * Lines reveal one at a time (tap, Space or →) — Brian, second rehearsal: "a lot
 * of words to read"; the words should arrive as he says them.
 *
 * Items are referenced by their POSITION on the section (1-based), not by id,
 * so the same deck serves the demo section and its rehearsal copy. A deck is
 * keyed by class id; a section without a deck gets the plain list as before.
 *
 * Built into the site for demo day. Authoring decks in the app is the next
 * build (status doc, 2026-09-22 feedback #2).
 */

const ATTEST_DEMO_DECK = {
  title: 'Attest — the class is the demo',
  slides: [
    {
      key: 'opening',
      title: 'Opening the class',
      lines: [
        'The class opened at its scheduled time. The code only works inside that window.',
        'Nobody typed a roster. You scanned, you tapped, you\u2019re on it.',
        'The instructor counts heads. The screen compares heads to check-ins.',
      ],
      items: [1],
    },
    {
      key: 'attestation',
      title: 'The attestation',
      lines: [
        'You tapped: \u201cI\u2019m actively affiliated with the agency listed.\u201d',
        'That\u2019s a signature \u2014 who, when, which device.',
        'Every class, a fresh tap. No pre-ticked boxes.',
        'No password. Your phone remembers you next time.',
      ],
      items: [10],
    },
    {
      key: 'affiliation',
      title: 'What counts as affiliation',
      lines: [
        'For the fee waiver, the college keeps one thing: your signed attestation.',
        'Paid or volunteer picks the waiver code.',
        'Federal law enforcement isn\u2019t eligible \u2014 they\u2019re invoiced.',
        'Don\u2019t know your A-B Tech ID? The office confirms it later. No date of birth, no SSN.',
      ],
      items: [7],
    },
    {
      key: 'capture',
      title: 'Did we capture it?',
      lines: [
        'For each person: check-in time, the attestation, every answer given or missed, verified minutes.',
        'A tailgate in the bay with no section yet? Still captured. The office builds the section after.',
        'Leave at the break and the system proposes Incomplete. The instructor decides.',
      ],
      items: [11, 5],
    },
    {
      key: 'census',
      title: 'The 10% date',
      lines: [
        'Regular schedule: hours \u00f7 10, from the start. Thirty hours \u2192 three hours in.',
        'Online, self-paced: hours \u00f7 4, counted back from the end date. First access is the evidence.',
        'HyFlex: in the room, or the assignment done \u2014 reported separately.',
        'Worked out and frozen with the record. Nobody retypes it.',
      ],
      items: [6],
    },
    {
      key: 'record',
      title: 'Where the record lives',
      lines: [
        'Attest: attendance, verification, completion.',
        'Colleague: identity, enrolment, transcript. A-B Tech owns the record.',
        'Nothing is edited. A correction is a new line with a reason.',
        'A card is good from the day of the class \u2014 not the day someone typed it in.',
      ],
      items: [2, 12],
    },
    {
      key: 'impact',
      title: 'What this changes for you',
      lines: [
        'Day-of registration goes away.',
        'The office works ahead: build the section, confirm IDs.',
        'The packet writes itself at the close \u2014 agreement, attendance record, signed roster, transcription sheet.',
        'Two polls: what matters most in a record, and what to connect first.',
      ],
      items: [3, 9],
    },
    {
      key: 'concerns',
      title: 'Concerns \u2014 say them here',
      lines: [
        'The honest list: one operator. No SOC 2 yet. Colleague write-back not built. A new vendor in the chain.',
        'Answers are anonymous. Say the hard thing.',
      ],
      items: [4, 8],
    },
    {
      key: 'final',
      title: 'Final assessment',
      lines: [
        'Three questions. The last one completes the class.',
        'Anyone who\u2019s left is proposed Incomplete \u2014 on screen, right now.',
      ],
      items: [13, 14, 15],
    },
    {
      key: 'close',
      title: 'Closing the class',
      lines: [
        'Took part \u2192 Was here \u2192 Close \u2192 Certify \u2192 Award.',
        'Then the packet \u2014 final the moment it\u2019s certified. Download it, hand it over.',
        'Then the Tracker moves, and the medic sees it on their phone.',
      ],
      items: [],
      closing: true,
    },
  ],
};

/** Class ids this deck is for: the demo section and its rehearsal copy. */
const DECKS = {
  'a270aafb-1b63-4495-a7aa-ef11a1a4e032': ATTEST_DEMO_DECK, // EMS 3000-DEMO22
  '21fe60e9-5562-437f-8dba-9e113eee8865': ATTEST_DEMO_DECK, // EMS 3000-REHRS
  'f1f83e48-22c9-4e9e-942f-1294d7eac6a0': ATTEST_DEMO_DECK, // EMS 3000-REHRS2
};

export function deckForClass(classId) {
  return (classId && DECKS[classId]) || null;
}

export { ATTEST_DEMO_DECK };
